// ---------------------------------------------------------------------------
// WaitlistForm Renderer (editor key: WaitlistForm)
// ---------------------------------------------------------------------------
// Renders a static waitlist signup form for pre-launch or capacity-limited
// offerings. The `action` URL routes through the URL safelist; if omitted
// (or rejected by the safelist) the form publishes as `disabled` so the
// page never silently posts to `#`. `currentCount` is clamped to a positive
// integer and formatted as a thousands-separated string at render time, so
// a runaway value cannot break the layout.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface WaitlistFormProps {
  heading?: string;
  description?: string;
  buttonLabel?: string;
  placeholder?: string;
  action?: string;
  currentCount?: number | string;
  countSuffix?: string;
}

const DEFAULT_HEADING = "Join the Waitlist";
const DEFAULT_BUTTON_LABEL = "Join Waitlist";
const DEFAULT_PLACEHOLDER = "Your email address";
const DEFAULT_COUNT_SUFFIX = "people already on the list";

const MAX_COUNT = 100_000_000;

function nonEmpty(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}

function clampCount(value: unknown): number | null {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) return null;
  if (parsed <= 0) return null;
  return Math.min(Math.floor(parsed), MAX_COUNT);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as WaitlistFormProps;

  const heading = nonEmpty(p.heading, DEFAULT_HEADING);
  const description = typeof p.description === "string" ? p.description.trim() : "";
  const buttonLabel = nonEmpty(p.buttonLabel, DEFAULT_BUTTON_LABEL);
  const placeholder = nonEmpty(p.placeholder, DEFAULT_PLACEHOLDER);
  const countSuffix = nonEmpty(p.countSuffix, DEFAULT_COUNT_SUFFIX);

  const rawAction = typeof p.action === "string" ? p.action.trim() : "";
  const sanitizedAction = rawAction ? url(rawAction) : "";
  const hasAction = sanitizedAction.length > 0 && sanitizedAction !== "#";
  const disabledAttrs = hasAction ? "" : ' disabled aria-disabled="true"';

  const formAttrs = hasAction
    ? `action="${sanitizedAction}" method="post"`
    : `data-ease-waitlist-form="unconfigured"`;

  const descriptionHtml = description
    ? `\n    <p class="ease-waitlist-form__description">${text(description)}</p>`
    : "";

  const clampedCount = clampCount(p.currentCount);
  const countHtml = clampedCount !== null
    ? `\n    <p class="ease-waitlist-form__count"><span class="ease-waitlist-form__count-value">${text(clampedCount.toLocaleString("en-US"))}</span> <span class="ease-waitlist-form__count-suffix">${text(countSuffix)}</span></p>`
    : "";

  return `<section class="ease-section ease-waitlist-form">
  <div class="ease-waitlist-form__inner">
    <h2 class="ease-waitlist-form__heading">${text(heading)}</h2>${descriptionHtml}
    <form class="ease-waitlist-form__form" ${formAttrs}>
      <label class="ease-waitlist-form__field">
        <span class="ease-waitlist-form__label">${text(placeholder)}</span>
        <input class="ease-waitlist-form__input" type="email" name="email" placeholder="${attr(placeholder)}" autocomplete="email" required${disabledAttrs}>
      </label>
      <button class="ease-waitlist-form__submit ease-btn ease-btn-primary" type="submit"${disabledAttrs}>${text(buttonLabel)}</button>
    </form>${countHtml}
  </div>
</section>`;
}

registerRenderer("waitlist-form", { render });
